import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getMswEvalNoteForm,
  updateMswEvalNoteForm,
} from "../../../../../services/patientService";
import {
  DeleteIcon,
  FileCheckIcon,
  HelpingIcon,
  HomeIcon,
  MedicalNotesIcon,
  PlusIcon,
  TargetIcon,
  UsersIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  MSW_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ADDITIONAL_CRS_LTC_OPTIONS,
  CARE_MANAGEMENT_OPTIONS,
  EMOTIONAL_PRESENTATION_OPTIONS,
  FUTURE_PLAN_OPTIONS,
  HEALTH_INSURANCE_OPTIONS,
  LIVING_ARRANGEMENT_OPTIONS,
  LIVING_CONDITION_OPTIONS,
  LIVING_VENUE_OPTIONS,
  MEALS_PROVIDER_OPTIONS,
  MENTAL_STATUS_OPTIONS,
  MSW_EVAL_ARRAY_KEYS,
  MSW_EVAL_TEXT_LIST_KEYS,
  PERSONS_PRESENT_OPTIONS,
  PT_COPING_SKILLS_OPTIONS,
  RESOURCE_STATUS_OPTIONS,
  RESOURCE_STATUS_ROWS,
  TRANSPORTATION_OPTIONS,
  addTextItem,
  emptyMswEvalPayload,
  migrateLegacyMswEvalFields,
  normalizeResourceStatuses,
  normalizeTextItems,
  removeTextItem,
  serializeTextItems,
  toggleInList,
} from "./mswEvalNoteContent";
import MswNotesCommonHeader from "./MswNotesCommonHeader";
import {
  normalizeMswCommonHeaderState,
  resolveMswNotesCommonHeader,
  withNormalizedMswCommonHeader,
} from "./mswNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "../nurseNotes/nurseVisitCommon";

const EVAL_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyMswEvalPayload(),
  evalDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const TextItemCard = ({
  title,
  accentClass,
  items,
  canEdit,
  kind,
  placeholder,
  onChangeText,
  onAdd,
  onRemove,
}) => (
  <div
    className={`rounded-md border border-slate-200 bg-white overflow-hidden border-l-4 ${accentClass}`}
  >
    <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/70">
      <SubHeading>{title}</SubHeading>
      {canEdit ? (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
        >
          <PlusIcon size={12} />
          Add
        </button>
      ) : null}
    </div>
    <div className="p-2.5 space-y-2">
      {(items || []).map((row, idx) => (
        <div key={row.id} className="flex gap-2 min-w-0">
          <span
            className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600"
            aria-hidden
          >
            {idx + 1}
          </span>
          <textarea
            rows={2}
            className={`${textareaClass} min-w-0 flex-1`}
            value={row.text || ""}
            disabled={!canEdit}
            placeholder={placeholder}
            aria-label={`${title} ${idx + 1}`}
            onChange={(e) => onChangeText(row.id, e.target.value)}
          />
          {canEdit ? (
            <button
              type="button"
              disabled={(items || []).length <= 1}
              onClick={() => onRemove(row.id)}
              className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
              aria-label={`Remove ${kind} ${idx + 1}`}
            >
              <DeleteIcon size={14} />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  </div>
);

TextItemCard.propTypes = {
  title: PropTypes.string.isRequired,
  accentClass: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, text: PropTypes.string }),
  ),
  canEdit: PropTypes.bool,
  kind: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChangeText: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ChipMultiSelect = ({ options, values, canEdit, onToggle, name }) => {
  const selected = Array.isArray(values) ? values : [];
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={name}>
      {options.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={!canEdit}
            onClick={() => canEdit && onToggle(opt)}
            className={`inline-flex items-center px-3 py-1.5 rounded-md border text-[12px] font-medium transition-colors ${
              checked
                ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            } ${canEdit ? "cursor-pointer" : "cursor-default opacity-90"}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

ChipMultiSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  name: PropTypes.string,
};

const MswEvalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyMswEvalPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};
      const legacyClinical = migrateLegacyMswEvalFields(loadedPayload);

      const merged = {
        ...base,
        ...loadedPayload,
        ...legacyClinical,
        ...pickNurseVisitMeta(loadedPayload, data),
        evalDate: data.evalDate || loadedPayload.evalDate || data.visitDate || today(),
        commonHeader: normalizeMswCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        problems: normalizeTextItems(loadedPayload.problems, {
          kind: "problem",
          minCount: 1,
        }),
        goals: normalizeTextItems(loadedPayload.goals, {
          kind: "goal",
          minCount: 1,
        }),
        interventions: normalizeTextItems(loadedPayload.interventions, {
          kind: "intervention",
          minCount: 1,
        }),
        outcomes: normalizeTextItems(loadedPayload.outcomes, {
          kind: "outcome",
          minCount: 1,
        }),
        resourceStatuses: normalizeResourceStatuses(
          loadedPayload.resourceStatuses || base.resourceStatuses,
        ),
        ptCopingSkills: loadedPayload.ptCopingSkills || "",
        monthlyIncome: loadedPayload.monthlyIncome || "",
        frequencyDuration: loadedPayload.frequencyDuration || "",
        livingArrangement:
          legacyClinical.livingArrangement ||
          loadedPayload.livingArrangement ||
          base.livingArrangement,
        livingVenue:
          legacyClinical.livingVenue || loadedPayload.livingVenue || "",
        careManagement:
          legacyClinical.careManagement || loadedPayload.careManagement || "",
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      MSW_EVAL_ARRAY_KEYS.forEach((key) => {
        const fromLegacy = Array.isArray(legacyClinical[key])
          ? legacyClinical[key]
          : null;
        const fromLoaded = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : null;
        merged[key] = fromLoaded?.length
          ? fromLoaded
          : fromLegacy?.length
            ? fromLegacy
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveMswNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "MSW Evaluation Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            evalDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyMswEvalPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getMswEvalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveMswNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateTextListItem = (listKey, itemId, text) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((row) =>
        row.id === itemId ? { ...row, text } : row,
      ),
    }));
  };

  const handleAddTextItem = (listKey, kind) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: addTextItem(prev[listKey], kind),
    }));
  };

  const handleRemoveTextItem = (listKey, kind, itemId) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: removeTextItem(prev[listKey], itemId, kind, { minCount: 1 }),
    }));
  };

  const setResourceStatus = (key, status) => {
    setValues((prev) => ({
      ...prev,
      resourceStatuses: {
        ...normalizeResourceStatuses(prev.resourceStatuses),
        [key]: status,
      },
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        MSW_FORM_EDITOR_ROLES,
        "View only — only MSW can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      evalDate,
      ...mswFields
    } = values;

    const nestedPayload = withNormalizedMswCommonHeader({
      ...mswFields,
      problems: serializeTextItems(values.problems),
      goals: serializeTextItems(values.goals),
      interventions: serializeTextItems(values.interventions),
      outcomes: serializeTextItems(values.outcomes),
      resourceStatuses: normalizeResourceStatuses(values.resourceStatuses),
      ptCopingSkills: values.ptCopingSkills || "",
      monthlyIncome: values.monthlyIncome || "",
      frequencyDuration: values.frequencyDuration || "",
    });

    MSW_EVAL_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    MSW_EVAL_TEXT_LIST_KEYS.forEach((key) => {
      nestedPayload[key] = serializeTextItems(values[key]);
    });

    const payload = {
      flag,
      evalDate: evalDate ? formatDateForInput(evalDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateMswEvalNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.evalDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            MSW Evaluation Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health clinical documentation for medical social work evaluation.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth || form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Evaluation Date"
          dateValue={displayDateUS(values.evalDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Evaluation Date
                </div>
                <InputDatePicker
                  name="evalDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.evalDate) || ""}
                  onChange={(event) => setValue("evalDate", event.target.value)}
                  max={EVAL_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <MswNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={UsersIcon} title="7. Clinical Intake">
          <SubHeading>Persons Present at Visit</SubHeading>
          <CheckboxGrid
            options={PERSONS_PRESENT_OPTIONS}
            values={values.personsPresent}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("personsPresent", opt)}
            columns={4}
          />
        </Section>

        <Section icon={HomeIcon} title="8. Psychosocial & Living Situation">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Living Arrangement">
              <select
                className={fieldClass}
                value={values.livingArrangement || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("livingArrangement", e.target.value)}
              >
                <option value="">Select…</option>
                {LIVING_ARRANGEMENT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Living Venue">
              <select
                className={fieldClass}
                value={values.livingVenue || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("livingVenue", e.target.value)}
              >
                <option value="">Select…</option>
                {LIVING_VENUE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Care Management">
              <select
                className={fieldClass}
                value={values.careManagement || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("careManagement", e.target.value)}
              >
                <option value="">Select…</option>
                {CARE_MANAGEMENT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Living Condition</SubHeading>
            <CheckboxGrid
              options={LIVING_CONDITION_OPTIONS}
              values={values.livingCondition}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("livingCondition", opt)}
              columns={3}
            />
          </div>
        </Section>

        <Section icon={HelpingIcon} title="9. Daily Living & Support">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <SubHeading>Meals Provider</SubHeading>
              <CheckboxGrid
                options={MEALS_PROVIDER_OPTIONS}
                values={values.mealsProvider}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("mealsProvider", opt)}
                columns={1}
              />
            </div>
            <div>
              <SubHeading>Transportation</SubHeading>
              <CheckboxGrid
                options={TRANSPORTATION_OPTIONS}
                values={values.transportation}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("transportation", opt)}
                columns={1}
              />
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="10. Clinical Problems & Goals">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <TextItemCard
              title="Problem"
              accentClass="border-l-sky-500"
              items={values.problems}
              canEdit={canEdit}
              kind="problem"
              placeholder="Describe clinical problem…"
              onChangeText={(id, text) =>
                updateTextListItem("problems", id, text)
              }
              onAdd={() => handleAddTextItem("problems", "problem")}
              onRemove={(id) =>
                handleRemoveTextItem("problems", "problem", id)
              }
            />
            <TextItemCard
              title="Goal"
              accentClass="border-l-emerald-500"
              items={values.goals}
              canEdit={canEdit}
              kind="goal"
              placeholder="Describe measurable goal…"
              onChangeText={(id, text) => updateTextListItem("goals", id, text)}
              onAdd={() => handleAddTextItem("goals", "goal")}
              onRemove={(id) => handleRemoveTextItem("goals", "goal", id)}
            />
            <TextItemCard
              title="Intervention"
              accentClass="border-l-indigo-500"
              items={values.interventions}
              canEdit={canEdit}
              kind="intervention"
              placeholder="Describe intervention…"
              onChangeText={(id, text) =>
                updateTextListItem("interventions", id, text)
              }
              onAdd={() => handleAddTextItem("interventions", "intervention")}
              onRemove={(id) =>
                handleRemoveTextItem("interventions", "intervention", id)
              }
            />
            <TextItemCard
              title="Outcome"
              accentClass="border-l-amber-500"
              items={values.outcomes}
              canEdit={canEdit}
              kind="outcome"
              placeholder="Describe outcome…"
              onChangeText={(id, text) =>
                updateTextListItem("outcomes", id, text)
              }
              onAdd={() => handleAddTextItem("outcomes", "outcome")}
              onRemove={(id) =>
                handleRemoveTextItem("outcomes", "outcome", id)
              }
            />
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="11. Financial & Insurance">
          <Field label="Monthly Income">
            <input
              type="text"
              className={fieldClass}
              value={values.monthlyIncome || ""}
              disabled={!canEdit}
              placeholder="e.g. $ 3,300.00"
              onChange={(e) => setValue("monthlyIncome", e.target.value)}
            />
          </Field>
          <div className="mt-3">
            <SubHeading>Health Insurance</SubHeading>
            <CheckboxGrid
              options={HEALTH_INSURANCE_OPTIONS}
              values={values.healthInsurance}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("healthInsurance", opt)}
              columns={2}
            />
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="12. Emotional & Mental Health">
          <div className="space-y-3">
            <div>
              <SubHeading>Mental Status</SubHeading>
              <CheckboxGrid
                options={MENTAL_STATUS_OPTIONS}
                values={values.mentalStatus}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("mentalStatus", opt)}
                columns={4}
              />
            </div>
            <div>
              <SubHeading>Emotional Presentation</SubHeading>
              <ChipMultiSelect
                name="emotionalPresentation"
                options={EMOTIONAL_PRESENTATION_OPTIONS}
                values={values.emotionalPresentation}
                canEdit={canEdit}
                onToggle={(opt) =>
                  toggleListValue("emotionalPresentation", opt)
                }
              />
            </div>
            <div>
              <SubHeading>Pt Coping Skills</SubHeading>
              <ExclusiveChoiceGrid
                name="ptCopingSkills"
                options={PT_COPING_SKILLS_OPTIONS}
                value={values.ptCopingSkills || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("ptCopingSkills", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="13. Plan & Community Resources">
          <div className="space-y-3">
            <div className="space-y-2.5">
              {RESOURCE_STATUS_ROWS.map((row) => (
                <div
                  key={row.key}
                  className="rounded-md border border-slate-200 bg-slate-50/40 px-2.5 py-2"
                >
                  <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
                    {row.label}
                  </div>
                  <ExclusiveChoiceGrid
                    name={`resource-${row.key}`}
                    options={RESOURCE_STATUS_OPTIONS}
                    value={values.resourceStatuses?.[row.key] || ""}
                    canEdit={canEdit}
                    onChange={(opt) => setResourceStatus(row.key, opt)}
                    columns={3}
                  />
                </div>
              ))}
            </div>

            <div>
              <SubHeading>Additional CRS &amp; LTC Options Addressed</SubHeading>
              <CheckboxGrid
                options={ADDITIONAL_CRS_LTC_OPTIONS}
                values={values.additionalCrsLtc}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("additionalCrsLtc", opt)}
                columns={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <SubHeading>Future Plan</SubHeading>
                <CheckboxGrid
                  options={FUTURE_PLAN_OPTIONS}
                  values={values.futurePlan}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("futurePlan", opt)}
                  columns={2}
                />
              </div>
              <Field label="Frequency & Duration">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.frequencyDuration || ""}
                  disabled={!canEdit}
                  placeholder="e.g. 1x/week x 4 weeks"
                  onChange={(e) =>
                    setValue("frequencyDuration", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
          clinicianSectionTitle="Authentication & Certification"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

MswEvalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default MswEvalNoteForm;
